/**
 * Listener for iOS Share Extension.
 * Handles both shared text (roster paste) and shared images (roster screenshots).
 *
 * The native share extension saves content to App Group storage
 * and opens the main app. The AppDelegate reads it and fires
 * CustomEvents on window:
 *   - `shared-roster-text` for text
 *   - `shared-roster-image` for images
 */

import type { ImageInput } from '../types';

type SharedTextHandler = (text: string) => void;
type SharedImageHandler = (image: ImageInput) => void;

let textHandler: SharedTextHandler | null = null;
let imageHandler: SharedImageHandler | null = null;
let pendingText: string | null = null;
let pendingImage: ImageInput | null = null;

/** Register the handler for shared text. */
export function onSharedRosterText(fn: SharedTextHandler): () => void {
  textHandler = fn;

  if (pendingText) {
    const text = pendingText;
    pendingText = null;
    fn(text);
  }

  return () => { textHandler = null; };
}

/** Register the handler for shared images. */
export function onSharedRosterImage(fn: SharedImageHandler): () => void {
  imageHandler = fn;

  if (pendingImage) {
    const img = pendingImage;
    pendingImage = null;
    fn(img);
  }

  return () => { imageHandler = null; };
}

/** Initialize the global listeners. Call once on app startup. */
export function initShareExtensionListener(): void {
  window.addEventListener('shared-roster-text', ((event: CustomEvent<{ text: string }>) => {
    const text = event.detail?.text;
    if (!text) return;

    if (textHandler) {
      textHandler(text);
    } else {
      pendingText = text;
    }
  }) as EventListener);

  window.addEventListener('shared-roster-image', ((event: CustomEvent<{ base64: string; mediaType: string }>) => {
    const { base64, mediaType } = event.detail ?? {};
    if (!base64) return;

    const image: ImageInput = {
      base64Data: base64,
      mediaType: (mediaType || 'image/jpeg') as ImageInput['mediaType'],
    };

    if (imageHandler) {
      imageHandler(image);
    } else {
      pendingImage = image;
    }
  }) as EventListener);

  // Signal to native code that React listeners are ready
  (window as any).__rosterBridgeReady = true;
}
